/**
 * B-28 (porteur, 06/10/2026) : le signataire agit SUR le document.
 *
 * Demande, mot pour mot : « un menu à droite de l'affichage du PDF avec
 * toutes les options ou les boutons, et garder la totalité de la hauteur de
 * l'écran pour le PDF » ; « les champs doivent être cliquables » ; « un
 * bouton large à la fin du document : je valide et signe et je veux recevoir
 * mon code, ou je refuse de signer ».
 *
 * Ce qui ne change pas : l'acte de signature reste la validation du code ;
 * le consentement reste un texte du serveur et une case jamais pre-cochee ;
 * chaque page doit avoir ete affichee (Franck, 21/10/2026, qui remplace
 * « atteindre le bas » de B-19). Le serveur verifie les trois de son cote :
 * rien ici ne contourne une etape.
 */
import { useState, type CSSProperties } from "react";

import { ApiError, CHAMPS_AFFICHES, nomDuRepere, postChamp, postPiece, postConsent, postDocumentReviewed, type Emplacement, type SignerView } from "../api";
import type { t } from "../i18n";
import type { RepereInfo } from "../components/PdfViewer";
import { ErrorPanel } from "../components/ErrorPanel";
import { ActionsDiscretes, Ico } from "../components/Icone";
import { Otp } from "./Otp";

type L = ReturnType<typeof t>;

interface MenuProps {
  view: SignerView;
  L: L;
  reperes: RepereInfo[];
  faits: ReadonlySet<number>;
  pageCount: number;
  page: number;
  zoom: number;
  onZoom: (z: number) => void;
  /** Porteur, 13/10/2026 : precedente / suivante pour passer d'une page a l'autre. */
  onPage: (page: number) => void;
  onAller: (rang: number) => void;
  onFin: () => void;
  onDownload: () => void;
  onPrint: () => void;
  /** Paraphes pas encore apposes, et leur validation en un clic (B-28,
   *  porteur 11/10/2026 : « une validation pour toutes les pages »). */
  paraphesRestants: number;
  onParapherTout: () => void;
}

/* Retours du porteur (06/10/2026) : outils sur une ligne, plus de liste des
   emplacements (illisible quand il y en a beaucoup) mais Precedent /
   Suivant et le prochain a valider ; plus de compteur de pages. */
export function Menu({
  view, L, reperes, faits, pageCount, page, zoom, onZoom, onPage,
  onAller, onFin, onDownload, onPrint, paraphesRestants, onParapherTout,
}: MenuProps) {
  // Seuls les cadres ou le signataire AGIT (signature, saisies) se
  // parcourent ; date, nom et e-mail s'affichent sur le document.
  const actions = reperes.flatMap((r, i) => (CHAMPS_AFFICHES.includes(r.kind) ? [] : [i]));
  const [courant, setCourant] = useState(0);
  const n = actions.length;
  const c = Math.min(courant, Math.max(0, n - 1));
  const rang = actions[c] ?? -1;
  const r = reperes[rang];
  const faitsActions = actions.filter((i) => faits.has(i)).length;
  const prochain = actions.findIndex((i) => !faits.has(i));
  const aller = (k: number) => {
    setCourant(k);
    onAller(actions[k]!);
  };

  return (
    <aside className="parcours__menu">
      <section className="card menu__bloc menu__entete">
        {view.issuer_name && (
          <p className="menu__emetteur">{view.issuer_name} {L.asksYouToSign}</p>
        )}
        <h1 className="menu__document">{view.title}</h1>
        {(view.message_subject || view.message_body) && (
          <div className="issuer-message">
            {view.message_subject && <p style={{ margin: "0 0 0.5rem", fontWeight: 600 }}>{view.message_subject}</p>}
            {view.message_body && <p style={{ margin: 0 }}>{view.message_body}</p>}
          </div>
        )}
        <div className="barre-outils" role="toolbar">
          <span className="barre-outils__groupe">
            <button type="button" aria-label={L.pagePrecedente} title={L.pagePrecedente} disabled={page <= 1}
                    onClick={() => onPage(Math.max(1, page - 1))}><Ico nom="gauche" /></button>
            <span className="barre-outils__page">{L.page} {page} / {pageCount || "…"}</span>
            <button type="button" aria-label={L.pageSuivante} title={L.pageSuivante}
                    disabled={!pageCount || page >= pageCount}
                    onClick={() => onPage(Math.min(pageCount, page + 1))}><Ico nom="droite" /></button>
          </span>
          <span className="barre-outils__groupe">
            <button type="button" aria-label={L.zoomOut} title={L.zoomOut}
                    onClick={() => onZoom(Math.max(0.6, +(zoom - 0.2).toFixed(2)))}>−</button>
            <span className="barre-outils__zoom">{Math.round(zoom * 100)} %</span>
            <button type="button" aria-label={L.zoomIn} title={L.zoomIn}
                    onClick={() => onZoom(Math.min(3, +(zoom + 0.2).toFixed(2)))}>+</button>
          </span>
        </div>
        {/* Porteur, 11/10/2026 : « Télécharger » et « Imprimer » écrits en toutes lettres. */}
        <div className="menu__actions-doc">
          <button type="button" className="btn btn--secondary" onClick={onDownload}>
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M8 2v8m0 0L4.5 6.5M8 10l3.5-3.5M3 13h10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {L.download}
          </button>
          <button type="button" className="btn btn--secondary" onClick={onPrint}>
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M4.5 6V2.5h7V6M4.5 11.5H3A1 1 0 0 1 2 10.5v-3.5a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v3.5a1 1 0 0 1-1 1h-1.5M4.5 9.5h7v4h-7z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>
            {L.print}
          </button>
        </div>
      </section>

      <section className="card stack menu__bloc">
        {n > 0 && r && (
          <>
            <p className="menu__compte">{L.validesSur(faitsActions, n)}</p>
            <div className="jauge" aria-hidden="true">
              <div className="jauge__remplie" style={{ "--part": faitsActions / n } as CSSProperties} />
            </div>
            <div className="menu__nav">
              <button type="button" className="icon-btn" aria-label={L.precedent} title={L.precedent}
                      onClick={() => aller((c - 1 + n) % n)}><Ico nom="gauche" /></button>
              <button type="button" className={faits.has(rang) ? "menu__champ menu__champ--fait" : "menu__champ"}
                      onClick={() => aller(c)}>
                <Ico nom={faits.has(rang) ? "coche" : "rond"} />
                <span>{nomDuRepere(L.reperes, r)} · {L.pageDuDoc(r.page)}</span>
                <span className="menu__rang">{c + 1}/{n}</span>
              </button>
              <button type="button" className="icon-btn" aria-label={L.suivant} title={L.suivant}
                      onClick={() => aller((c + 1) % n)}><Ico nom="droite" /></button>
            </div>
          </>
        )}
        {prochain >= 0 ? (
          <button type="button" className="btn btn--primary" onClick={() => aller(prochain)}>
            {L.prochainAValider(nomDuRepere(L.reperes, reperes[actions[prochain]!]!), reperes[actions[prochain]!]!.page)}
          </button>
        ) : (
          <button type="button" className="btn btn--primary" onClick={onFin}>{L.allerValider} <Ico nom="bas" /></button>
        )}
        {paraphesRestants > 1 && (
          <button type="button" className="btn btn--secondary" onClick={onParapherTout}>
            {L.parapherTout(paraphesRestants)}
          </button>
        )}
      </section>
    </aside>
  );
}

interface FinProps {
  token: string;
  view: SignerView;
  L: L;
  pagesVues: number[];
  /** Toutes les pages vues (ou lecture deja enregistree par le serveur). */
  toutesVues: boolean;
  pageCount: number;
  premiereNonVue?: number;
  onAllerALaPage: (page: number) => void;
  restants: number;
  onSuivant: () => void;
  onDecline: () => void;
  onSigned: () => void;
  onRecharger: () => void;
  onChoisirSignature: () => void;
  /** 15/10/2026 : l'emetteur autorise ce destinataire a transmettre la
   *  signature ; absent quand c'est interdit (rien ne s'affiche). */
  onTransmettre?: () => void;
}

/** Sous la derniere page : consentement, puis les deux gros boutons. */
export function Fin({ token, view, L, pagesVues, toutesVues, pageCount, premiereNonVue, onAllerALaPage, restants,
  onSuivant, onDecline, onSigned, onRecharger, onChoisirSignature, onTransmettre }: FinProps) {
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<ApiError | null>(null);
  // Retour sur le lien apres consentement et choix du visuel : on reprend
  // a la saisie du code, sans tout redemander.
  // B-13 : l'approbateur ne choisit pas de visuel de signature.
  const approbateur = view.role === "approver";
  const [code, setCode] = useState(view.consent_given && (approbateur || Boolean(view.signature_methode)));
  const [immediat, setImmediat] = useState(false);

  const vues = pagesVues.filter((n) => n >= 1 && n <= pageCount).length;
  const pret = toutesVues && restants === 0 && (approbateur || Boolean(view.signature_methode));

  const valider = async () => {
    setEnvoi(true);
    setErreur(null);
    const sansConflit = async (appel: () => Promise<unknown>) => {
      try {
        await appel();
      } catch (e) {
        // Deja enregistre (double clic, onglet rouvert) : l'etape est faite.
        if (!(e instanceof ApiError && e.kind === "conflict")) throw e;
      }
    };
    try {
      if (!view.reviewed) await sansConflit(() => postDocumentReviewed(token, pagesVues, "defilement"));
      if (!view.consent_given) await sansConflit(() => postConsent(token));
      setImmediat(true);
      setCode(true);
      onRecharger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e : new ApiError("server", 0, null));
    } finally {
      setEnvoi(false);
    }
  };

  if (code) {
    return (
      <section className="parcours__fin card stack" id="fin-du-document">
        <Otp token={token} view={view} L={L} onSigned={onSigned} onDecline={onDecline} envoiImmediat={immediat} />
      </section>
    );
  }

  return (
    <section className="parcours__fin card stack" id="fin-du-document">
      {!view.signature_methode && !approbateur && (
        <div className="alert alert--warning">
          <p style={{ margin: 0 }}>{L.signatureAChoisir}</p>
          <div className="actions">
            <button type="button" className="btn btn--secondary" onClick={onChoisirSignature}>
              {L.choisirSignature}
            </button>
          </div>
        </div>
      )}
      {erreur && <ErrorPanel error={erreur} L={L} />}
      {!toutesVues && pageCount > 0 && (
        // Le bouton bloque dit pourquoi, et mene a la page qui manque.
        <div className="lecture-a-finir">
          <p className="lecture__compteur" aria-live="polite">
            {L.pagesSeen(vues, pageCount)}
          </p>
          <div className="jauge" aria-hidden="true">
            <div className="jauge__remplie"
                 style={{ "--part": vues / pageCount } as CSSProperties} />
          </div>
          <p className="muted">{L.readAllPages}</p>
          {premiereNonVue && (
            <button type="button" className="btn btn--secondary" onClick={() => onAllerALaPage(premiereNonVue)}>
              {L.afficherLaPage(premiereNonVue)} <Ico nom="haut" />
            </button>
          )}
        </div>
      )}
      {/* Porteur, 11/10/2026 (« à quoi sert ce bloc ? ») : plus d'encadre
          a part - tant qu'un emplacement reste, le gros bouton le dit et y
          mene. */}
      {restants > 0 ? (
        <button type="button" className="btn btn--primary btn--large" onClick={onSuivant}>
          {L.restants(restants)} <Ico nom="haut" />
        </button>
      ) : (
        // Le texte du consentement (version du serveur) est DANS le bouton :
        // cliquer, c'est consentir - un seul geste, jamais pre-fait.
        <button type="button" className="btn btn--primary btn--large btn--consentir" disabled={!pret || envoi}
                onClick={() => void valider()}>
          <strong className="btn--consentir__action">{view.cle_acces ? L.validerEtSignerCle : L.validerEtSigner}</strong>
          <span className="btn--consentir__texte">{view.consentement?.case ?? L.consentCheckbox}</span>
        </button>
      )}
      <ActionsDiscretes onTransmettre={onTransmettre} transmettre={L.transmettre} onDecline={onDecline}
                        refuser={L.refuserDeSigner} />
    </section>
  );
}

/** Lot 3bis : saisie d'un texte ou d'une mention, dans une fenetre sur le
 *  document. La valeur est enregistree tout de suite (elle survit a un
 *  rechargement) et n'entre dans le PDF qu'a la signature. */
export function Saisie({ token, cadre, L, onAnnuler, onFait }: {
  token: string;
  cadre: Emplacement;
  L: L;
  onAnnuler: () => void;
  onFait: () => void;
}) {
  const [texte, setTexte] = useState(cadre.valeur ?? "");
  const [fichier, setFichier] = useState<File | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const mention = cadre.kind === "mention";

  const enregistrer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cadre.kind === "piece_jointe" ? !fichier && cadre.obligatoire && !cadre.valeur
      : cadre.obligatoire && !texte.trim()) {
      setErreur(L.saisieObligatoire);
      return;
    }
    setEnvoi(true);
    setErreur(null);
    try {
      if (cadre.kind === "piece_jointe") {
        if (fichier) await postPiece(token, cadre.id, fichier);
      } else {
        await postChamp(token, cadre.id, texte.trim() || null);
      }
      onFait();
    } catch (err) {
      setErreur(err instanceof ApiError && err.detail ? err.detail : L.saisieEchec);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <form className="stack" onSubmit={(e) => void enregistrer(e)}>
      <h2 className="card__title">{nomDuRepere(L.reperes, cadre)}</h2>
      {mention ? (
        <p style={{ margin: 0 }}>{L.recopiez} <strong>« {cadre.libelle} »</strong></p>
      ) : (
        // La consigne d'un texte est deja le titre.
        cadre.libelle && !["texte", "liste", "piece_jointe"].includes(cadre.kind)
          && <p style={{ margin: 0 }}>{cadre.libelle}</p>
      )}
      {cadre.kind === "piece_jointe" ? (
        <>
          {cadre.valeur && <p style={{ margin: 0 }}><Ico nom="coche" /> {cadre.valeur}</p>}
          <input className="saisie" type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                 aria-label={cadre.libelle ?? L.reperes[cadre.kind]}
                 onChange={(e) => setFichier(e.target.files?.[0] ?? null)} />
          <p className="muted" style={{ margin: 0 }}>{L.pieceFormats}</p>
        </>
      ) : cadre.kind === "date_saisie" ? (
        // Le calendrier du navigateur ; enregistree en JJ/MM/AAAA.
        <input className="saisie" type="date" autoFocus
               value={/^\d{2}\/\d{2}\/\d{4}$/.test(texte) ? texte.split("/").reverse().join("-") : ""}
               onChange={(e) => setTexte(e.target.value ? e.target.value.split("-").reverse().join("/") : "")}
               aria-label={cadre.libelle ?? L.reperes[cadre.kind]} aria-invalid={erreur ? true : undefined} />
      ) : cadre.kind === "liste" ? (
        // Liste deroulante : un des choix de l'emetteur, rien d'autre (le
        // serveur le verifie aussi).
        <select className="saisie" autoFocus value={texte} onChange={(e) => setTexte(e.target.value)}
                aria-label={cadre.libelle ?? L.reperes[cadre.kind]} aria-invalid={erreur ? true : undefined}>
          <option value="">{L.choisir}</option>
          {(cadre.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          className="saisie"
          autoFocus
          value={texte}
          maxLength={500}
          onChange={(e) => setTexte(e.target.value)}
          aria-label={cadre.libelle ?? L.reperes[cadre.kind]}
          aria-invalid={erreur ? true : undefined}
        />
      )}
      {!cadre.obligatoire && <p className="muted" style={{ margin: 0 }}>{L.facultatif}</p>}
      {erreur && <p className="alert alert--error" role="alert" style={{ margin: 0 }}>{erreur}</p>}
      <div className="actions">
        <button type="submit" className="btn btn--primary" disabled={envoi}>{L.enregistrer}</button>
        <button type="button" className="btn btn--secondary" onClick={onAnnuler}>{L.annuler}</button>
      </div>
    </form>
  );
}
